import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDate } from "../../../lib/format";
import { DateField } from "../../../components/common/DateField";

export const AreaCard = ({ area }) => {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [form, setForm] = useState({ weekdays: area.weekdays, cutoff: area.cutoff, holidays: area.holidays });
  const [busy, setBusy] = useState(false);
  const wd = t("admin.wd");
  const toggle = (d) => setForm((f) => ({ ...f, weekdays: f.weekdays.includes(d) ? f.weekdays.filter((x) => x !== d) : [...f.weekdays, d].sort() }));
  const addHoliday = (v) => v && setForm((f) => ({ ...f, holidays: [...new Set([...f.holidays, v])].sort() }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/delivery/areas/${area.code}`, form);
      qc.invalidateQueries({ queryKey: ["deliveryAreas"] });
      toast.success(t("admin.saved"));
    } catch (e) { toast.error(t("common.error")); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 rounded-2xl bg-white p-4 shadow-sm" data-testid={`area-card-${area.code}`}>
      <h3 className="text-base font-semibold text-navy">{area[`name_${lang}`]}</h3>
      <div className="flex flex-wrap gap-1.5">
        {wd.map((label, d) => (
          <button key={label} type="button" onClick={() => toggle(d)} data-testid={`area-${area.code}-wd-${d}`}
            className={`h-11 w-12 rounded-xl text-sm font-semibold transition-colors ${form.weekdays.includes(d) ? "bg-brand text-white" : "bg-slate-100 text-slate-500"}`}>{label}</button>
        ))}
      </div>
      <label className="block text-xs text-slate-500">{t("admin.cutoff")}
        <input type="text" inputMode="numeric" pattern="([01]\d|2[0-3]):[0-5]\d" maxLength={5} placeholder="14:00" value={form.cutoff} onChange={(e) => setForm((f) => ({ ...f, cutoff: e.target.value }))} data-testid={`area-${area.code}-cutoff`} className="mt-1 h-11 w-full rounded-xl border border-slate-200 px-3 text-sm text-navy" />
      </label>
      <div>
        <p className="text-xs text-slate-500">{t("admin.holidays")}</p>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {form.holidays.map((h) => (
            <span key={h} className="flex h-8 items-center gap-1 rounded-full bg-amber-100 pl-3 pr-1 text-xs font-medium text-amber-800">
              {fmtDate(h)}<button type="button" aria-label={t("admin.remove")} onClick={() => setForm((f) => ({ ...f, holidays: f.holidays.filter((x) => x !== h) }))} className="grid h-6 w-6 place-items-center rounded-full hover:bg-amber-200"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
        <DateField value="" onChange={addHoliday} testId={`area-${area.code}-holiday`} />
      </div>
      <button type="button" disabled={busy} onClick={save} data-testid={`area-${area.code}-save`} className="h-11 w-full rounded-full bg-navy text-sm font-semibold text-white disabled:opacity-60">{t("admin.save")}</button>
    </div>
  );
};
